import React, { useState } from "react";
import { Button } from "../ui/button";
import {
  Plus,
  Loader2,
  X,
  Users,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { services } from "../../services";
import { useToast } from "../ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface UserApprovalSummary {
  total: number;
  processedFreshCount: number;
  successful: number;
  failed: number;
  skipped: number;
}

interface UserApprovalData {
  userId: string;
  walletAddress: string;
  success: boolean;
  kycHash: string;
  complianceHash: string;
}

interface UserApprovalResponse {
  message: string;
  status: boolean;
  data: UserApprovalData[];
  count: number;
  summary: UserApprovalSummary;
}

export default function ApproveUserAutomate() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [walletAddresses, setWalletAddresses] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setWalletAddresses("");
  };

  const handleApproveUsers = async () => {
    if (!walletAddresses.trim()) {
      toast({
        title: "Error",
        description: "Please enter at least one wallet address",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Parse wallet addresses (comma-separated or newline-separated)
      const addresses = walletAddresses
        .split(/[,\n]/)
        .map((addr) => addr.trim())
        .filter((addr) => addr.length > 0);

      if (addresses.length === 0) {
        toast({
          title: "Error",
          description: "Please enter valid wallet addresses",
          variant: "destructive",
        });
        return;
      }

      const response = await services.post("/approve-user-auto/process-users", {
        approve: true,
        walletAddresses: addresses,
      });

      const responseData = response.data as UserApprovalResponse;

      if (responseData && responseData.status) {
        // Show success toast with summary
        const { summary } = responseData;
        const message = `User approval completed! Total: ${summary.total}, Successful: ${summary.successful}, Failed: ${summary.failed}, Skipped: ${summary.skipped}`;

        toast({
          title: responseData.message || "User Approval Summary",
          description: message,
          variant: "default",
        });

        // Log detailed results for debugging

        // Close dialog on success
        handleCloseDialog();

        // Refresh the page after successful approval
        window.location.reload();
      } else {
        toast({
          title: "Error",
          description: responseData?.message || "Failed to approve users",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve users. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="flex justify-end">
        <Button
          className="flex items-center gap-3 bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md px-6 py-3 rounded-lg transform hover:scale-105"
          onClick={handleOpenDialog}
          disabled={isLoading}
        >
          <Users className="h-5 w-5" />
          <span className="font-semibold">Approve Users</span>
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-white border-0 shadow-2xl">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-gradientButton rounded-full flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-800 text-center">
              Approve Users
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-base text-center">
              Enter wallet addresses to approve. You can separate multiple
              addresses with commas or new lines.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 text-center">
            <div className="space-y-3">
              <Label
                htmlFor="walletAddresses"
                className="text-sm font-semibold text-gray-700 flex items-center justify-center gap-2"
              >
                <CheckCircle className="h-4 w-4 text-primary" />
                Wallet Addresses
              </Label>
              <div className="relative">
                <textarea
                  id="walletAddresses"
                  value={walletAddresses}
                  onChange={(e) => setWalletAddresses(e.target.value)}
                  placeholder="Enter wallet addresses separated by commas or new lines&#10;&#10;Example:&#10;0x1234...5678&#10;0xabcd...efgh&#10;0x9876...5432"
                  className="w-full min-h-[140px] p-4 border-1 border-gray-100 rounded-xl resize-vertical focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 bg-gray-50 focus:bg-white text-sm font-mono"
                  disabled={isProcessing}
                />
                {walletAddresses && (
                  <div className="absolute top-2 right-2 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                    {
                      walletAddresses
                        .split(/[,\n]/)
                        .filter((addr) => addr.trim()).length
                    }{" "}
                    addresses
                  </div>
                )}
              </div>
            </div>

            {walletAddresses && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <div className="flex items-center justify-center gap-2 text-primary text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">Preview:</span>
                </div>
                <div className="mt-2 text-xs text-primary/80 font-mono text-center">
                  {walletAddresses
                    .split(/[,\n]/)
                    .filter((addr) => addr.trim())
                    .slice(0, 3)
                    .map((addr, index) => (
                      <div key={index} className="truncate">
                        {addr.trim()}
                      </div>
                    ))}
                  {walletAddresses.split(/[,\n]/).filter((addr) => addr.trim())
                    .length > 3 && (
                    <div className="text-primary/70">
                      ... and{" "}
                      {walletAddresses
                        .split(/[,\n]/)
                        .filter((addr) => addr.trim()).length - 3}{" "}
                      more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-3 pt-6">
            <Button
              variant="outline"
              onClick={handleCloseDialog}
              disabled={isProcessing}
              className="flex-1 h-12 border border-gray-200 hover:border-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveUsers}
              disabled={isProcessing || !walletAddresses.trim()}
              className="flex-1 h-12 bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md rounded-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Approve Users
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
